<script setup>
import { computed, onMounted, ref } from 'vue';
import { RouterLink, useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { useAdmin } from 'dashboard/composables/useAdmin';
import CrmTasksAPI from 'dashboard/api/crmTasks';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';

const OPEN_STATUSES = ['open', 'in_progress', 'waiting'];
const STATUS_LABELS = {
  open: 'Aberta',
  in_progress: 'Em andamento',
  waiting: 'Aguardando',
  completed: 'Concluida',
  canceled: 'Cancelada',
};
const STATUS_CLASSES = {
  open: 'bg-[#EAF6F1] text-[#176B55]',
  in_progress: 'bg-[#E8F0FE] text-[#2457A6]',
  waiting: 'bg-[#FFF4D8] text-[#8A5A00]',
  completed: 'bg-[#EAF7EA] text-[#2D6A2D]',
  canceled: 'bg-[#F2F4F7] text-[#5F6673]',
};
const PRIORITY_LABELS = {
  low: 'Baixa',
  normal: 'Normal',
  high: 'Alta',
  urgent: 'Urgente',
};
const PRIORITY_CLASSES = {
  low: 'bg-[#F2F4F7] text-[#5F6673]',
  normal: 'bg-[#EAF6F1] text-[#176B55]',
  high: 'bg-[#FFF0E6] text-[#A84D13]',
  urgent: 'bg-[#FDEBEC] text-[#B42318]',
};
const WAITING_LABELS = {
  agent: 'Corretor',
  customer: 'Cliente',
  partner: 'Parceiro',
  system: 'Sistema',
  none: 'Sem espera',
};
const TASK_TYPE_LABELS = {
  silent_lead_follow_up: 'Lead sem resposta',
  confirm_visit: 'Confirmar visita',
  return_property_key: 'Chave a devolver',
  decide_property_offer: 'Decidir proposta',
  collect_pending_documentation: 'Documento pendente',
};
const SORT_OPTIONS = [
  'due_at,asc',
  'priority,desc',
  'updated_at,desc',
  'created_at,desc',
];
const EVENT_LABELS = {
  created: 'Criada',
  updated: 'Atualizada',
  assigned: 'Atribuida',
  completed: 'Concluida',
  canceled: 'Cancelada',
  reopened: 'Reaberta',
  waiting: 'Aguardando',
  started: 'Iniciada',
  sla_breached: 'SLA vencido',
};
const SLA_TIMER_STATUS_LABELS = {
  running: 'Rodando',
  paused: 'Pausado',
  breached: 'Violado',
  acknowledged: 'Reconhecido',
  resolved: 'Resolvido',
  canceled: 'Cancelado',
};
const SLA_BREACH_STATUS_LABELS = {
  open: 'Aberto',
  acknowledged: 'Reconhecido',
  recovered: 'Recuperado',
};
const MIN_SLA_REASON_LENGTH = 12;
const CRM_TASK_AUTOMATION_EVENTS = [
  { key: 'crm_task_created' },
  { key: 'crm_task_updated' },
  { key: 'crm_task_status_changed' },
  { key: 'crm_task_assigned' },
  { key: 'crm_task_started' },
  { key: 'crm_task_waiting' },
  { key: 'crm_task_reopened' },
  { key: 'crm_task_completed' },
  { key: 'crm_task_canceled' },
  { key: 'crm_task_sla_breached' },
];

const routeString = value => (Array.isArray(value) ? value[0] : value);
const routeBoolean = (value, fallback) => {
  const normalized = String(routeString(value) || '').toLowerCase();
  if (['1', 'true', 'yes'].includes(normalized)) return true;
  if (['0', 'false', 'no'].includes(normalized)) return false;
  return fallback;
};
const routeNumber = (value, fallback) => {
  const parsed = Number(routeString(value));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};
const routeChoice = (value, choices, fallback = '') => {
  const normalized = routeString(value);
  return choices.includes(normalized) ? normalized : fallback;
};
const compactQuery = query =>
  Object.fromEntries(
    Object.entries(query).filter(([, value]) => value != null && value !== '')
  );
const querySignature = query =>
  Object.entries(query)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => `${key}:${Array.isArray(value) ? value[0] : value}`)
    .join('|');
const defaultFilters = (query = {}) => {
  const status = routeChoice(query.status, Object.keys(STATUS_LABELS));

  return {
    open: routeBoolean(query.open, !status),
    overdue: routeBoolean(query.overdue, false),
    status,
    task_type: routeChoice(query.task_type, Object.keys(TASK_TYPE_LABELS)),
    priority: routeChoice(query.priority, Object.keys(PRIORITY_LABELS)),
    waiting_on: routeChoice(query.waiting_on, Object.keys(WAITING_LABELS)),
    assignee_id: routeString(query.assignee_id) || '',
    team_id: routeString(query.team_id) || '',
    sort: routeChoice(query.sort, SORT_OPTIONS, 'due_at,asc'),
    page: routeNumber(query.page, 1),
    per_page: routeNumber(query.per_page, 25),
  };
};
const defaultSlaSummary = () => ({
  metrics: {
    timers: {
      active: 0,
      running: 0,
      due_soon: 0,
      overdue: 0,
      paused: 0,
      breached: 0,
      acknowledged: 0,
    },
    breaches: {
      active: 0,
      open: 0,
      acknowledged: 0,
      escalated: 0,
    },
    pauses: {
      active: 0,
      closed: 0,
    },
    redistribution: {
      capacity_reassigned: 0,
      retained_without_candidate: 0,
      territory_reassigned: 0,
    },
  },
  timers: [],
});

const route = useRoute();
const router = useRouter();
const account = useAccount();
const { t } = useI18n();
const { isAdmin } = useAdmin();
const currentUser = useMapGetter('getCurrentUser');
const showAutomationMenu = ref(false);
const crmTaskAutomationEventNames = new Set(
  CRM_TASK_AUTOMATION_EVENTS.map(({ key }) => key)
);
const automationMenuItems = computed(() => [
  {
    action: 'create_automation',
    value: 'crm_task_created',
    label: t('AUTOMATION.EVENTS.CRM_TASK_CREATED'),
    icon: 'i-lucide-workflow',
  },
  {
    action: 'create_automation',
    value: 'crm_task_updated',
    label: t('AUTOMATION.EVENTS.CRM_TASK_UPDATED'),
    icon: 'i-lucide-workflow',
  },
  {
    action: 'create_automation',
    value: 'crm_task_status_changed',
    label: t('AUTOMATION.EVENTS.CRM_TASK_STATUS_CHANGED'),
    icon: 'i-lucide-workflow',
  },
  {
    action: 'create_automation',
    value: 'crm_task_assigned',
    label: t('AUTOMATION.EVENTS.CRM_TASK_ASSIGNED'),
    icon: 'i-lucide-workflow',
  },
  {
    action: 'create_automation',
    value: 'crm_task_started',
    label: t('AUTOMATION.EVENTS.CRM_TASK_STARTED'),
    icon: 'i-lucide-workflow',
  },
  {
    action: 'create_automation',
    value: 'crm_task_waiting',
    label: t('AUTOMATION.EVENTS.CRM_TASK_WAITING'),
    icon: 'i-lucide-workflow',
  },
  {
    action: 'create_automation',
    value: 'crm_task_reopened',
    label: t('AUTOMATION.EVENTS.CRM_TASK_REOPENED'),
    icon: 'i-lucide-workflow',
  },
  {
    action: 'create_automation',
    value: 'crm_task_completed',
    label: t('AUTOMATION.EVENTS.CRM_TASK_COMPLETED'),
    icon: 'i-lucide-workflow',
  },
  {
    action: 'create_automation',
    value: 'crm_task_canceled',
    label: t('AUTOMATION.EVENTS.CRM_TASK_CANCELED'),
    icon: 'i-lucide-workflow',
  },
  {
    action: 'create_automation',
    value: 'crm_task_sla_breached',
    label: t('AUTOMATION.EVENTS.CRM_TASK_SLA_BREACHED'),
    icon: 'i-lucide-workflow',
  },
]);
const tasks = ref([]);
const meta = ref({ count: 0, assignees: [], teams: [] });
const slaSummary = ref(defaultSlaSummary());
const filters = ref(defaultFilters(route.query));
const selectedTask = ref(null);
const selectedTaskId = ref(null);
const assignmentForm = ref({ assignee_id: '', team_id: '' });
const slaActionReasons = ref({});
const isLoading = ref(false);
const isLoadingDetail = ref(false);
const isLoadingSlaSummary = ref(false);
const isMutating = ref(false);
const isSlaActionMutating = ref(false);

const assigneeOptions = computed(() => [
  { id: '', name: 'Sem responsavel' },
  ...(meta.value.assignees || []),
]);
const teamOptions = computed(() => [
  { id: '', name: 'Sem time' },
  ...(meta.value.teams || []),
]);
const hasTasks = computed(() => tasks.value.length > 0);
const selectedEvents = computed(() => selectedTask.value?.events || []);
const slaMetrics = computed(
  () => slaSummary.value?.metrics || defaultSlaSummary().metrics
);
const slaTimers = computed(() => slaSummary.value?.timers || []);
const selectedTaskSlaTimers = computed(() =>
  slaTimers.value.filter(timer => timer.crm_task?.id === selectedTask.value?.id)
);
const selectedIsOpen = computed(() =>
  OPEN_STATUSES.includes(selectedTask.value?.status)
);
const currentUserId = computed(() => currentUser.value?.id || '');
const activeQuickFilter = computed(() => {
  if (filters.value.overdue) return 'overdue';
  if (
    filters.value.assignee_id &&
    filters.value.assignee_id === currentUserId.value
  )
    return 'mine';
  if (filters.value.status === 'completed') return 'completed';
  if (filters.value.open) return 'open';
  return 'all';
});
const stats = computed(() => [
  {
    key: 'total',
    label: 'Total filtrado',
    value: meta.value.count ?? tasks.value.length,
    icon: 'i-lucide-list-checks',
  },
  {
    key: 'open',
    label: 'Abertas',
    value: tasks.value.filter(task => OPEN_STATUSES.includes(task.status))
      .length,
    icon: 'i-lucide-circle-dot',
  },
  {
    key: 'overdue',
    label: 'Vencidas',
    value: tasks.value.filter(task => task.overdue || task.sla_breached_at)
      .length,
    icon: 'i-lucide-alarm-clock',
  },
]);
const slaStats = computed(() => [
  {
    key: 'active_timers',
    label: 'Timers ativos',
    value: slaMetrics.value.timers?.active || 0,
    icon: 'i-lucide-timer',
  },
  {
    key: 'paused_timers',
    label: 'Pausados',
    value:
      slaMetrics.value.pauses?.active || slaMetrics.value.timers?.paused || 0,
    icon: 'i-lucide-pause-circle',
  },
  {
    key: 'active_breaches',
    label: 'Breaches ativos',
    value: slaMetrics.value.breaches?.active || 0,
    icon: 'i-lucide-siren',
  },
  {
    key: 'capacity_reassigned',
    label: 'Redistribuicoes',
    value: slaMetrics.value.redistribution?.capacity_reassigned || 0,
    icon: 'i-lucide-route',
  },
]);

const labelFor = (collection, key) => collection[key] || key || 'Nao informado';
const statusLabel = status => labelFor(STATUS_LABELS, status);
const priorityLabel = priority => labelFor(PRIORITY_LABELS, priority);
const waitingLabel = waitingOn => labelFor(WAITING_LABELS, waitingOn);
const taskTypeLabel = taskType => labelFor(TASK_TYPE_LABELS, taskType);
const eventLabel = eventType => labelFor(EVENT_LABELS, eventType);
const slaTimerStatusLabel = status => labelFor(SLA_TIMER_STATUS_LABELS, status);
const slaBreachStatusLabel = status =>
  labelFor(SLA_BREACH_STATUS_LABELS, status);
const badgeClass = (collection, key) =>
  collection[key] || 'bg-[#F2F4F7] text-[#5F6673]';
const taskAssigneeLabel = task => task?.assignee?.name || 'Sem responsavel';
const taskSlaTimer = task =>
  slaTimers.value.find(timer => timer.crm_task?.id === task?.id);
const activeBreaches = timer => timer?.active_breaches || [];
const slaActionReason = breach =>
  (slaActionReasons.value[breach.id] || '').trim();
const canSubmitSlaAction = breach =>
  slaActionReason(breach).length >= MIN_SLA_REASON_LENGTH;
const capacityStatusLabel = capacity => {
  if (!capacity) return 'Capacidade nao avaliada';
  if (capacity.status === 'reassigned') return 'Redistribuido por capacidade';
  if (capacity.status === 'retained') return 'Responsavel mantido';
  return capacity.status || 'Capacidade registrada';
};
const durationLabel = seconds => {
  const value = Number(seconds || 0);
  if (value < 60) return `${value}s`;
  if (value < 3600) return `${Math.floor(value / 60)}min`;
  return `${Math.floor(value / 3600)}h ${Math.floor((value % 3600) / 60)}min`;
};
const localizedDate = value => {
  if (!value) return 'Sem prazo';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Data invalida';
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(parsed);
};
const taskParams = () => ({
  ...filters.value,
  open: filters.value.open,
  overdue: filters.value.overdue,
});
const taskRouteQuery = () =>
  compactQuery({
    ...route.query,
    open: filters.value.open ? undefined : '0',
    overdue: filters.value.overdue ? '1' : undefined,
    status: filters.value.status || undefined,
    task_type: filters.value.task_type || undefined,
    priority: filters.value.priority || undefined,
    waiting_on: filters.value.waiting_on || undefined,
    assignee_id: filters.value.assignee_id || undefined,
    team_id: filters.value.team_id || undefined,
    sort: filters.value.sort === 'due_at,asc' ? undefined : filters.value.sort,
    page: filters.value.page > 1 ? String(filters.value.page) : undefined,
    per_page:
      filters.value.per_page === 25
        ? undefined
        : String(filters.value.per_page),
  });
const syncTaskRouteState = () => {
  const nextQuery = taskRouteQuery();
  if (querySignature(route.query) === querySignature(nextQuery)) return;
  router.replace({ query: nextQuery }).catch(() => {});
};
const taskContextLinks = task => {
  if (!task) return [];
  return [
    {
      key: 'conversation',
      label: 'Conversa',
      icon: 'i-lucide-message-circle',
      href: task.conversation_id
        ? account.accountScopedUrl(`conversations/${task.conversation_id}`)
        : '',
    },
    {
      key: 'contact',
      label: 'Contato',
      icon: 'i-lucide-user-round',
      href: task.contact_id
        ? account.accountScopedUrl(`history/${task.contact_id}`)
        : '',
    },
    {
      key: 'imovel',
      label: 'Imovel',
      icon: 'i-lucide-building-2',
      href: task.imovel_id
        ? account.accountScopedUrl(`imoveis/${task.imovel_id}`)
        : '',
    },
  ].filter(link => link.href);
};
const syncAssignmentForm = task => {
  assignmentForm.value = {
    assignee_id: task?.assignee_id || '',
    team_id: task?.team_id || '',
  };
};
const patchTask = task => {
  tasks.value = tasks.value.map(item => (item.id === task.id ? task : item));
  selectedTask.value = task;
  selectedTaskId.value = task.id;
  syncAssignmentForm(task);
};
const loadTaskDetail = async id => {
  if (!id) return;
  isLoadingDetail.value = true;
  try {
    const { data } = await CrmTasksAPI.show(id);
    patchTask(data);
  } catch {
    useAlert('Nao foi possivel carregar a auditoria da tarefa CRM.');
  } finally {
    isLoadingDetail.value = false;
  }
};
const loadSlaSummary = async () => {
  isLoadingSlaSummary.value = true;
  try {
    const { data } = await CrmTasksAPI.slaSummary({
      ...taskParams(),
      limit: 50,
    });
    slaSummary.value = data || defaultSlaSummary();
  } catch {
    slaSummary.value = defaultSlaSummary();
    useAlert('Nao foi possivel carregar o resumo SLA das tarefas.');
  } finally {
    isLoadingSlaSummary.value = false;
  }
};
const loadTasks = async (options = {}) => {
  if (options?.syncRoute !== false) syncTaskRouteState();

  isLoading.value = true;
  try {
    const [{ data }] = await Promise.all([
      CrmTasksAPI.list(taskParams()),
      loadSlaSummary(),
    ]);
    tasks.value = data.data || [];
    meta.value = data.meta || {
      count: tasks.value.length,
      assignees: [],
      teams: [],
    };

    const nextTask =
      tasks.value.find(task => task.id === selectedTaskId.value) ||
      tasks.value[0];
    if (nextTask) await loadTaskDetail(nextTask.id);
    else {
      selectedTask.value = null;
      selectedTaskId.value = null;
      syncAssignmentForm(null);
    }
  } catch {
    useAlert('Nao foi possivel carregar as tarefas CRM.');
  } finally {
    isLoading.value = false;
  }
};
const selectTask = task => {
  selectedTaskId.value = task.id;
  loadTaskDetail(task.id);
};
const applyQuickFilter = key => {
  filters.value = {
    ...filters.value,
    open: key !== 'all' && key !== 'completed',
    overdue: key === 'overdue',
    status: key === 'completed' ? 'completed' : '',
    assignee_id: key === 'mine' ? currentUserId.value : '',
    page: 1,
  };
  loadTasks();
};
const applyFilters = () => {
  if (filters.value.status) filters.value.open = false;
  filters.value.page = 1;
  loadTasks();
};
const runCommand = async command => {
  if (!selectedTask.value) return;
  isMutating.value = true;
  try {
    const { data } = await CrmTasksAPI[command](selectedTask.value.id);
    patchTask(data);
    useAlert('Tarefa CRM atualizada.');
  } catch {
    useAlert('Nao foi possivel atualizar a tarefa CRM.');
  } finally {
    isMutating.value = false;
  }
};
const saveAssignment = async () => {
  if (!selectedTask.value) return;
  isMutating.value = true;
  try {
    const { data } = await CrmTasksAPI.assign(selectedTask.value.id, {
      assignee_id: assignmentForm.value.assignee_id || null,
      team_id: assignmentForm.value.team_id || null,
    });
    patchTask(data);
    useAlert('Responsavel da tarefa atualizado.');
  } catch {
    useAlert('Nao foi possivel reatribuir a tarefa CRM.');
  } finally {
    isMutating.value = false;
  }
};
const runSlaBreachAction = async (action, breach) => {
  if (!selectedTask.value || !breach) return;

  const reason = slaActionReason(breach);
  if (reason.length < MIN_SLA_REASON_LENGTH) {
    useAlert('Informe um motivo operacional com pelo menos 12 caracteres.');
    return;
  }

  isSlaActionMutating.value = true;
  try {
    if (action === 'acknowledge') {
      await CrmTasksAPI.acknowledgeSlaBreach({
        taskId: selectedTask.value.id,
        breachId: breach.id,
        reason,
      });
    } else {
      await CrmTasksAPI.recoverSlaBreach({
        taskId: selectedTask.value.id,
        breachId: breach.id,
        reason,
      });
    }

    delete slaActionReasons.value[breach.id];
    await loadSlaSummary();
    await loadTaskDetail(selectedTask.value.id);
    useAlert(
      action === 'acknowledge'
        ? 'Breach SLA reconhecido.'
        : 'Breach SLA recuperado.'
    );
  } catch {
    useAlert('Nao foi possivel executar a acao SLA da tarefa.');
  } finally {
    isSlaActionMutating.value = false;
  }
};

const createAutomationForCrmTaskEvent = eventName => {
  if (!isAdmin.value || !crmTaskAutomationEventNames.has(eventName)) return;

  showAutomationMenu.value = false;
  router.push({
    name: 'automation_list',
    params: { accountId: account.accountId.value },
    query: {
      create: '1',
      automation_event: eventName,
    },
  });
};

const handleAutomationMenuAction = ({ action, value }) => {
  if (action !== 'create_automation') return;
  createAutomationForCrmTaskEvent(value);
};

onMounted(loadTasks);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <main class="min-h-screen bg-lais-bg text-lais-text">
    <LaisHeader title="Tarefas CRM" title-badge="P14">
      <template #actions>
        <div
          v-if="isAdmin"
          v-on-clickaway="() => (showAutomationMenu = false)"
          class="relative"
        >
          <LaisPrimaryButton
            icon="i-lucide-workflow"
            variant="ghost"
            aria-haspopup="menu"
            :aria-expanded="showAutomationMenu ? 'true' : 'false'"
            @click="showAutomationMenu = !showAutomationMenu"
          >
            {{ t('AUTOMATION.HEADER_BTN_TXT') }}
          </LaisPrimaryButton>
          <DropdownMenu
            v-if="showAutomationMenu"
            :menu-items="automationMenuItems"
            class="right-0 top-full mt-1 max-h-80 w-64 overflow-y-auto"
            @action="handleAutomationMenuAction"
          />
        </div>
        <LaisPrimaryButton
          icon="i-lucide-refresh-cw"
          variant="ghost"
          :is-loading="isLoading"
          @click="loadTasks"
        >
          Atualizar
        </LaisPrimaryButton>
      </template>
    </LaisHeader>

    <section class="px-4 pb-8 sm:px-6 lg:px-10">
      <div class="grid gap-3 md:grid-cols-3">
        <article
          v-for="stat in stats"
          :key="stat.key"
          class="flex min-w-0 items-center gap-3 rounded-lais border border-lais-border-soft bg-white p-4 shadow-lais-card"
        >
          <span
            class="grid size-10 shrink-0 place-items-center rounded-full bg-[#EAF6F1] text-[#176B55]"
            aria-hidden="true"
          >
            <span class="size-5" :class="stat.icon" />
          </span>
          <div class="min-w-0">
            <p class="text-[12px] font-semibold uppercase text-lais-text-muted">
              {{ stat.label }}
            </p>
            <strong class="text-[24px] leading-tight text-lais-text">
              {{ stat.value }}
            </strong>
          </div>
        </article>
      </div>

      <div class="mt-3 grid gap-3 md:grid-cols-4">
        <article
          v-for="stat in slaStats"
          :key="stat.key"
          class="flex min-w-0 items-center gap-3 rounded-lais border border-lais-border-soft bg-white p-4 shadow-lais-card"
        >
          <span
            class="grid size-9 shrink-0 place-items-center rounded-full bg-[#FFF4D8] text-[#8A5A00]"
            aria-hidden="true"
          >
            <span class="size-4" :class="stat.icon" />
          </span>
          <div class="min-w-0">
            <p class="text-[12px] font-semibold uppercase text-lais-text-muted">
              {{ stat.label }}
            </p>
            <strong class="text-[22px] leading-tight text-lais-text">
              {{ stat.value }}
            </strong>
          </div>
        </article>
      </div>

      <section
        class="mt-4 flex flex-col gap-3 rounded-lais border border-lais-border-soft bg-white p-3 shadow-lais-card"
      >
        <div class="flex min-w-0 flex-wrap gap-2">
          <button
            v-for="quick in [
              { key: 'open', label: 'Abertas' },
              { key: 'overdue', label: 'Vencidas' },
              { key: 'mine', label: 'Minhas' },
              { key: 'completed', label: 'Concluidas' },
              { key: 'all', label: 'Todas' },
            ]"
            :key="quick.key"
            type="button"
            class="inline-flex min-h-9 items-center gap-2 rounded-lais border px-3 text-[13px] font-semibold transition"
            :class="
              activeQuickFilter === quick.key
                ? 'border-[#176B55] bg-[#EAF6F1] text-[#176B55]'
                : 'border-lais-border-soft bg-white text-lais-text-body hover:border-[#176B55]'
            "
            @click="applyQuickFilter(quick.key)"
          >
            <span
              class="size-3.5"
              :class="
                quick.key === 'overdue'
                  ? 'i-lucide-alarm-clock'
                  : 'i-lucide-list-filter'
              "
              aria-hidden="true"
            />
            {{ quick.label }}
          </button>
        </div>

        <div class="grid gap-3 md:grid-cols-3 xl:grid-cols-6">
          <label
            class="flex min-w-0 flex-col gap-1 text-[12px] font-semibold text-lais-text-muted"
          >
            Status
            <select
              v-model="filters.status"
              class="min-h-10 rounded-lais border border-lais-border-soft bg-white px-3 text-[14px] text-lais-text"
              @change="applyFilters"
            >
              <option value="">Qualquer status</option>
              <option value="open">Aberta</option>
              <option value="in_progress">Em andamento</option>
              <option value="waiting">Aguardando</option>
              <option value="completed">Concluida</option>
              <option value="canceled">Cancelada</option>
            </select>
          </label>
          <label
            class="flex min-w-0 flex-col gap-1 text-[12px] font-semibold text-lais-text-muted"
          >
            Prioridade
            <select
              v-model="filters.priority"
              class="min-h-10 rounded-lais border border-lais-border-soft bg-white px-3 text-[14px] text-lais-text"
              @change="applyFilters"
            >
              <option value="">Qualquer prioridade</option>
              <option value="low">Baixa</option>
              <option value="normal">Normal</option>
              <option value="high">Alta</option>
              <option value="urgent">Urgente</option>
            </select>
          </label>
          <label
            class="flex min-w-0 flex-col gap-1 text-[12px] font-semibold text-lais-text-muted"
          >
            Espera
            <select
              v-model="filters.waiting_on"
              class="min-h-10 rounded-lais border border-lais-border-soft bg-white px-3 text-[14px] text-lais-text"
              @change="applyFilters"
            >
              <option value="">Qualquer espera</option>
              <option value="agent">Corretor</option>
              <option value="customer">Cliente</option>
              <option value="partner">Parceiro</option>
              <option value="system">Sistema</option>
              <option value="none">Sem espera</option>
            </select>
          </label>
          <label
            class="flex min-w-0 flex-col gap-1 text-[12px] font-semibold text-lais-text-muted"
          >
            Responsavel
            <select
              v-model="filters.assignee_id"
              class="min-h-10 rounded-lais border border-lais-border-soft bg-white px-3 text-[14px] text-lais-text"
              @change="applyFilters"
            >
              <option
                v-for="option in assigneeOptions"
                :key="`filter-assignee-${option.id}`"
                :value="option.id"
              >
                {{ option.name }}
              </option>
            </select>
          </label>
          <label
            class="flex min-w-0 flex-col gap-1 text-[12px] font-semibold text-lais-text-muted"
          >
            Time
            <select
              v-model="filters.team_id"
              class="min-h-10 rounded-lais border border-lais-border-soft bg-white px-3 text-[14px] text-lais-text"
              @change="applyFilters"
            >
              <option
                v-for="option in teamOptions"
                :key="`filter-team-${option.id}`"
                :value="option.id"
              >
                {{ option.name }}
              </option>
            </select>
          </label>
          <label
            class="flex min-w-0 flex-col gap-1 text-[12px] font-semibold text-lais-text-muted"
          >
            Ordem
            <select
              v-model="filters.sort"
              class="min-h-10 rounded-lais border border-lais-border-soft bg-white px-3 text-[14px] text-lais-text"
              @change="applyFilters"
            >
              <option value="due_at,asc">Prazo proximo</option>
              <option value="priority,desc">Prioridade</option>
              <option value="updated_at,desc">Atualizacao recente</option>
              <option value="created_at,desc">Criacao recente</option>
            </select>
          </label>
        </div>
      </section>

      <section
        class="mt-4 grid min-w-0 gap-4 xl:grid-cols-[minmax(0,0.95fr)_minmax(420px,1.05fr)]"
      >
        <div class="flex min-w-0 flex-col gap-3">
          <div
            v-if="isLoading && !hasTasks"
            class="rounded-lais border border-lais-border-soft bg-white p-6 text-[14px] text-lais-text-muted"
          >
            Carregando tarefas...
          </div>
          <div
            v-else-if="!hasTasks"
            class="rounded-lais border border-lais-border-soft bg-white p-6 text-[14px] text-lais-text-muted"
          >
            Nenhuma tarefa encontrada.
          </div>

          <LaisCard
            v-for="task in tasks"
            :key="task.id"
            density="tight"
            selectable
            :selected="selectedTaskId === task.id"
            :aria-label="`Abrir tarefa ${task.title}`"
            @click="selectTask(task)"
          >
            <template #header>
              <div
                class="flex min-w-0 flex-1 items-start justify-between gap-3"
              >
                <div class="min-w-0">
                  <p class="truncate text-[15px] font-semibold text-lais-text">
                    {{ task.title }}
                  </p>
                  <p class="truncate text-[12px] text-lais-text-muted">
                    {{ taskTypeLabel(task.task_type) }} ·
                    {{ task.contact?.name || 'Contato sem nome' }}
                  </p>
                </div>
                <span
                  class="shrink-0 rounded-full px-2 py-1 text-[11px] font-bold"
                  :class="badgeClass(STATUS_CLASSES, task.status)"
                >
                  {{ statusLabel(task.status) }}
                </span>
              </div>
            </template>

            <div class="grid gap-3 sm:grid-cols-2">
              <div
                class="flex min-w-0 items-center gap-2 text-[13px] text-lais-text-body"
              >
                <span
                  class="i-lucide-user-round size-4 shrink-0 text-lais-text-muted"
                  aria-hidden="true"
                />
                <span class="truncate">{{ taskAssigneeLabel(task) }}</span>
              </div>
              <div
                class="flex min-w-0 items-center gap-2 text-[13px] text-lais-text-body"
              >
                <span
                  class="i-lucide-calendar-clock size-4 shrink-0 text-lais-text-muted"
                  aria-hidden="true"
                />
                <span class="truncate">{{ localizedDate(task.due_at) }}</span>
              </div>
              <div class="flex min-w-0 flex-wrap gap-2 sm:col-span-2">
                <span
                  class="rounded-full px-2 py-1 text-[11px] font-bold"
                  :class="badgeClass(PRIORITY_CLASSES, task.priority)"
                >
                  {{ priorityLabel(task.priority) }}
                </span>
                <span
                  class="rounded-full bg-[#E8F0FE] px-2 py-1 text-[11px] font-bold text-[#2457A6]"
                >
                  {{ waitingLabel(task.waiting_on) }}
                </span>
                <span
                  v-if="task.overdue || task.sla_breached_at"
                  class="inline-flex items-center gap-1 rounded-full bg-[#FDEBEC] px-2 py-1 text-[11px] font-bold text-[#B42318]"
                >
                  <span
                    class="i-lucide-alarm-clock size-3.5"
                    aria-hidden="true"
                  />
                  SLA
                </span>
                <span
                  v-if="taskSlaTimer(task)"
                  class="inline-flex items-center gap-1 rounded-full bg-[#FFF4D8] px-2 py-1 text-[11px] font-bold text-[#8A5A00]"
                >
                  <span class="i-lucide-timer size-3.5" aria-hidden="true" />
                  {{ slaTimerStatusLabel(taskSlaTimer(task).status) }}
                </span>
                <span
                  v-if="activeBreaches(taskSlaTimer(task)).length"
                  class="inline-flex items-center gap-1 rounded-full bg-[#FDEBEC] px-2 py-1 text-[11px] font-bold text-[#B42318]"
                >
                  <span class="i-lucide-siren size-3.5" aria-hidden="true" />
                  {{ activeBreaches(taskSlaTimer(task)).length }} breach
                </span>
              </div>
            </div>
          </LaisCard>
        </div>

        <LaisCard v-if="selectedTask" density="flush">
          <template #header>
            <div class="flex min-w-0 flex-1 items-start justify-between gap-3">
              <div class="min-w-0">
                <p class="truncate text-[17px] font-semibold text-lais-text">
                  {{ selectedTask.title }}
                </p>
                <p class="truncate text-[12px] text-lais-text-muted">
                  {{ taskTypeLabel(selectedTask.task_type) }} · #{{
                    selectedTask.id
                  }}
                </p>
              </div>
              <span
                class="shrink-0 rounded-full px-2 py-1 text-[11px] font-bold"
                :class="badgeClass(STATUS_CLASSES, selectedTask.status)"
              >
                {{ statusLabel(selectedTask.status) }}
              </span>
            </div>
          </template>

          <div class="space-y-5 p-5">
            <div class="flex min-w-0 flex-wrap gap-2">
              <span
                class="rounded-full px-2 py-1 text-[11px] font-bold"
                :class="badgeClass(PRIORITY_CLASSES, selectedTask.priority)"
              >
                {{ priorityLabel(selectedTask.priority) }}
              </span>
              <span
                class="rounded-full bg-[#E8F0FE] px-2 py-1 text-[11px] font-bold text-[#2457A6]"
              >
                {{ waitingLabel(selectedTask.waiting_on) }}
              </span>
              <span
                class="rounded-full bg-[#F2F4F7] px-2 py-1 text-[11px] font-bold text-[#5F6673]"
              >
                {{ localizedDate(selectedTask.due_at) }}
              </span>
              <span
                v-if="selectedTask.sla_breached_at"
                class="rounded-full bg-[#FDEBEC] px-2 py-1 text-[11px] font-bold text-[#B42318]"
              >
                SLA {{ localizedDate(selectedTask.sla_breached_at) }}
              </span>
            </div>

            <p
              v-if="selectedTask.description"
              class="text-[14px] leading-relaxed text-lais-text-body"
            >
              {{ selectedTask.description }}
            </p>

            <div class="flex min-w-0 flex-wrap gap-2">
              <RouterLink
                v-for="link in taskContextLinks(selectedTask)"
                :key="link.key"
                :to="link.href"
                class="inline-flex min-h-9 items-center gap-2 rounded-lais border border-lais-border-soft px-3 text-[13px] font-semibold text-lais-text-body hover:border-[#176B55] hover:text-[#176B55]"
              >
                <span class="size-4" :class="link.icon" aria-hidden="true" />
                {{ link.label }}
              </RouterLink>
            </div>

            <div class="flex min-w-0 flex-wrap gap-2">
              <LaisPrimaryButton
                v-if="selectedIsOpen"
                icon="i-lucide-check"
                :is-loading="isMutating"
                @click="runCommand('complete')"
              >
                Concluir
              </LaisPrimaryButton>
              <LaisPrimaryButton
                v-if="selectedIsOpen"
                icon="i-lucide-x"
                variant="ghost"
                :is-loading="isMutating"
                @click="runCommand('cancel')"
              >
                Cancelar
              </LaisPrimaryButton>
              <LaisPrimaryButton
                v-else
                icon="i-lucide-rotate-ccw"
                variant="ghost"
                :is-loading="isMutating"
                @click="runCommand('reopen')"
              >
                Reabrir
              </LaisPrimaryButton>
            </div>

            <section
              class="rounded-lais border border-lais-border-soft bg-[#FBFCFD] p-4"
            >
              <div class="mb-3 flex items-center gap-2">
                <span
                  class="i-lucide-timer size-4 text-lais-text-muted"
                  aria-hidden="true"
                />
                <h2 class="text-[15px] font-semibold text-lais-text">
                  SLA operacional
                </h2>
                <span
                  v-if="isLoadingSlaSummary"
                  class="i-lucide-loader-circle size-4 animate-spin text-lais-text-muted"
                  aria-hidden="true"
                />
              </div>

              <div
                v-if="!selectedTaskSlaTimers.length"
                class="rounded-lais border border-dashed border-lais-border-soft bg-white p-3 text-[13px] text-lais-text-muted"
              >
                Nenhum timer P56 ligado a esta tarefa.
              </div>

              <div v-else class="space-y-3">
                <article
                  v-for="timer in selectedTaskSlaTimers"
                  :key="timer.id"
                  class="rounded-lais border border-lais-border-soft bg-white p-3"
                >
                  <div class="flex min-w-0 flex-wrap items-center gap-2">
                    <span
                      class="rounded-full bg-[#FFF4D8] px-2 py-1 text-[11px] font-bold text-[#8A5A00]"
                    >
                      {{ slaTimerStatusLabel(timer.status) }}
                    </span>
                    <span
                      class="rounded-full bg-[#F2F4F7] px-2 py-1 text-[11px] font-bold text-[#5F6673]"
                    >
                      {{ timer.milestone }}
                    </span>
                    <span
                      v-if="timer.active_pause"
                      class="rounded-full bg-[#E8F0FE] px-2 py-1 text-[11px] font-bold text-[#2457A6]"
                    >
                      Pausa {{ timer.active_pause.reason }} ·
                      {{ durationLabel(timer.active_pause.elapsed_seconds) }}
                    </span>
                  </div>

                  <div
                    class="mt-3 grid gap-2 text-[13px] text-lais-text-body sm:grid-cols-2"
                  >
                    <p>
                      <span class="font-semibold text-lais-text">Prazo:</span>
                      {{ localizedDate(timer.due_at) }}
                    </p>
                    <p>
                      <span class="font-semibold text-lais-text">
                        Due soon:
                      </span>
                      {{ localizedDate(timer.due_soon_at) }}
                    </p>
                    <p>
                      <span class="font-semibold text-lais-text">
                        Owner timer:
                      </span>
                      {{ timer.owner?.name || 'Sem owner' }}
                    </p>
                    <p v-if="timer.next_escalation">
                      <span class="font-semibold text-lais-text">
                        Proxima escala:
                      </span>
                      Nivel {{ timer.next_escalation.level }} ·
                      {{ localizedDate(timer.next_escalation.due_at) }}
                    </p>
                  </div>

                  <div
                    v-if="activeBreaches(timer).length"
                    class="mt-3 space-y-2"
                  >
                    <div
                      v-for="breach in activeBreaches(timer)"
                      :key="breach.id"
                      class="rounded-lais border border-[#F6C5C8] bg-[#FFF7F7] p-3 text-[13px] text-[#8A1F17]"
                    >
                      <p class="font-semibold">
                        Nivel {{ breach.level }} ·
                        {{ slaBreachStatusLabel(breach.status) }} ·
                        {{ localizedDate(breach.breached_at) }}
                      </p>
                      <p class="mt-1">
                        Owner atual:
                        {{
                          breach.current_owner?.owner?.name || 'Nao resolvido'
                        }}
                        ·
                        {{
                          capacityStatusLabel(breach.current_owner?.capacity)
                        }}
                      </p>
                      <div class="mt-3 space-y-2">
                        <textarea
                          v-model="slaActionReasons[breach.id]"
                          :aria-label="`Motivo operacional breach ${breach.id}`"
                          class="min-h-20 w-full rounded-lais border border-[#F6C5C8] bg-white px-3 py-2 text-[13px] text-lais-text outline-none focus:border-[#B42318] focus:ring-2 focus:ring-[#FDEBEC]"
                          placeholder="Motivo operacional"
                        />
                        <div class="flex min-w-0 flex-wrap gap-2">
                          <LaisPrimaryButton
                            v-if="breach.status === 'open'"
                            icon="i-lucide-eye"
                            size="sm"
                            :disabled="
                              !canSubmitSlaAction(breach) || isSlaActionMutating
                            "
                            :is-loading="isSlaActionMutating"
                            @click="runSlaBreachAction('acknowledge', breach)"
                          >
                            Reconhecer
                          </LaisPrimaryButton>
                          <LaisPrimaryButton
                            icon="i-lucide-check-check"
                            size="sm"
                            variant="ghost"
                            :disabled="
                              !canSubmitSlaAction(breach) || isSlaActionMutating
                            "
                            :is-loading="isSlaActionMutating"
                            @click="runSlaBreachAction('recover', breach)"
                          >
                            Recuperar
                          </LaisPrimaryButton>
                        </div>
                      </div>
                    </div>
                  </div>
                </article>
              </div>
            </section>

            <section
              class="grid gap-3 rounded-lais border border-lais-border-soft bg-[#FBFCFD] p-4 md:grid-cols-[1fr_1fr_auto]"
            >
              <label
                class="flex min-w-0 flex-col gap-1 text-[12px] font-semibold text-lais-text-muted"
              >
                Responsavel
                <select
                  v-model="assignmentForm.assignee_id"
                  class="min-h-10 rounded-lais border border-lais-border-soft bg-white px-3 text-[14px] text-lais-text"
                >
                  <option
                    v-for="option in assigneeOptions"
                    :key="`assign-assignee-${option.id}`"
                    :value="option.id"
                  >
                    {{ option.name }}
                  </option>
                </select>
              </label>
              <label
                class="flex min-w-0 flex-col gap-1 text-[12px] font-semibold text-lais-text-muted"
              >
                Time
                <select
                  v-model="assignmentForm.team_id"
                  class="min-h-10 rounded-lais border border-lais-border-soft bg-white px-3 text-[14px] text-lais-text"
                >
                  <option
                    v-for="option in teamOptions"
                    :key="`assign-team-${option.id}`"
                    :value="option.id"
                  >
                    {{ option.name }}
                  </option>
                </select>
              </label>
              <div class="flex items-end">
                <LaisPrimaryButton
                  icon="i-lucide-user-check"
                  variant="ghost"
                  :is-loading="isMutating"
                  @click="saveAssignment"
                >
                  Salvar
                </LaisPrimaryButton>
              </div>
            </section>

            <section>
              <div class="mb-3 flex items-center gap-2">
                <span
                  class="i-lucide-history size-4 text-lais-text-muted"
                  aria-hidden="true"
                />
                <h2 class="text-[15px] font-semibold text-lais-text">
                  Auditoria
                </h2>
                <span
                  v-if="isLoadingDetail"
                  class="i-lucide-loader-circle size-4 animate-spin text-lais-text-muted"
                  aria-hidden="true"
                />
              </div>
              <ol class="space-y-3">
                <li
                  v-for="event in selectedEvents"
                  :key="event.id"
                  class="grid grid-cols-[auto_1fr] gap-3 rounded-lais border border-lais-border-soft bg-white p-3"
                >
                  <span
                    class="mt-0.5 grid size-8 place-items-center rounded-full bg-[#EAF6F1] text-[#176B55]"
                  >
                    <span
                      class="i-lucide-git-commit-horizontal size-4"
                      aria-hidden="true"
                    />
                  </span>
                  <div class="min-w-0">
                    <p class="text-[13px] font-semibold text-lais-text">
                      {{ eventLabel(event.event_type) }}
                    </p>
                    <p class="text-[12px] text-lais-text-muted">
                      {{ localizedDate(event.occurred_at || event.created_at) }}
                    </p>
                  </div>
                </li>
              </ol>
            </section>
          </div>
        </LaisCard>
      </section>
    </section>
  </main>
</template>
